import type { WatchSource } from 'vue';
import { ref } from 'vue';

import type { NotificationResultDTO } from '@nora-monorepo/nora-api-client';
import type { BBox } from '@nora-monorepo/shared';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { OperationBboxStateManager } from '#nora-ui/map/map/state-managers/OperationBboxStateManager';
import type { NotificationStateSnapshot, Persistable } from '#nora-ui/map/types/persistence.types';
import { useNotificationStore } from '#nora-ui/stores/notification-store';

export interface SelectedOverlayNotification {
  id: string;
  domain: string;
  domainId: string;
  path: string;
}

export class NotificationStateManager extends OperationBboxStateManager<NotificationResultDTO[]> implements Persistable<NotificationStateSnapshot> {
  private selectedNotificationRef = ref<SelectedOverlayNotification | null>(null);
  private notificationLayerVisibleRef = ref(true);

  get selectedNotification(): SelectedOverlayNotification | null {
    return this.selectedNotificationRef.value;
  }

  get notificationLayerVisible(): boolean {
    return this.notificationLayerVisibleRef.value;
  }

  /**
   * The fetched notifications across all shown operations, flattened for rendering.
   * Populated by load()/loadOperation() and used by the layer manager.
   */
  get notifications(): NotificationResultDTO[] {
    return [...this.dataByOperation.values()].flat();
  }

  constructor(map: NoraMap) {
    super(map);
  }

  setSelectedNotification(file: SelectedOverlayNotification): void {
    this.selectedNotificationRef.value = file;
  }

  clearSelectedNotification(): void {
    this.selectedNotificationRef.value = null;

    this.map.layers.notification.clearSelectedNotification();
  }

  setVisibility(isVisible: boolean): void {
    const wasVisible = this.notificationLayerVisibleRef.value;

    this.notificationLayerVisibleRef.value = isVisible;
    this.map.layers.notification.setVisibility(isVisible);

    if (isVisible && !wasVisible) {
      void this.loadCurrentBbox();
    }
  }

  toggleVisibility(): void {
    this.setVisibility(!this.notificationLayerVisibleRef.value);
  }

  protected override async fetchOperationData(bbox: BBox, operationId: string): Promise<NotificationResultDTO[]> {
    const notificationStore = useNotificationStore();
    const notifications = await notificationStore.getNotificationsByBbox(bbox, operationId);

    return notifications;
  }

  protected override refreshLayer(): void {
    this.map.layers.notification.load();
  }

  /**
   * Serialize the notification state to a plain object for persistence.
   */
  serialize(): NotificationStateSnapshot {
    return {
      layerVisible: this.notificationLayerVisibleRef.value,
      selectedNotification: this.selectedNotificationRef.value,
    };
  }

  /**
   * Restore notification state from a persisted snapshot.
   */
  deserialize(snapshot: NotificationStateSnapshot): void {
    this.notificationLayerVisibleRef.value = snapshot.layerVisible;
    this.selectedNotificationRef.value = snapshot.selectedNotification;

    // Apply visibility to the layer
    this.map.layers.notification.setVisibility(snapshot.layerVisible);
  }

  /**
   * Returns a watch source for triggering persistence when state changes.
   */
  getWatchSource(): WatchSource {
    return () => [
      this.notificationLayerVisibleRef.value,
      this.selectedNotificationRef.value,
    ];
  }

  /**
   * Load notification state by fetching notifications for every shown operation based on the
   * current map bbox.
   */
  async load(): Promise<void> {
    this.unload();

    await this.loadCurrentBbox();
  }

  override async loadCurrentBbox(): Promise<void> {
    if (!this.notificationLayerVisibleRef.value) {
      return;
    }

    await super.loadCurrentBbox();
  }

  override async loadOperation(operationId: string): Promise<void> {
    if (!this.notificationLayerVisibleRef.value) {
      return;
    }
    await super.loadOperation(operationId);
  }

  /**
   * Unload/clear all notification state.
   */
  unload(): void {
    this.selectedNotificationRef.value = null;
    this.notificationLayerVisibleRef.value = true;
    this.clearOperationData();
  }
}
